import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_BASE_URL } from "../config";
import { useAuth } from "../context/AuthContext";

async function fetchWithAuth(url, options = {}, token) {
  const headers = {
    "Content-Type": "application/json",
    ...options.headers,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, { ...options, headers });
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || "Request failed");
  }

  return data;
}

export function useAdminUsers() {
  const { token } = useAuth();

  return useQuery({
    queryKey: ["adminUsers"],
    queryFn: async () => {
      const data = await fetchWithAuth(`${API_BASE_URL}/api/v1/admin/users`, {}, token);
      return data.users;
    },
  });
}

export function useUpdateUserRole() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, role }) => {
      return fetchWithAuth(
        `${API_BASE_URL}/api/v1/admin/users/${userId}/role`,
        {
          method: "PATCH",
          body: JSON.stringify({ role }),
        },
        token
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminUsers"] });
    },
  });
}

export function useUpdateUserStatus() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, status }) => {
      return fetchWithAuth(
        `${API_BASE_URL}/api/v1/admin/users/${userId}/status`,
        {
          method: "PATCH",
          body: JSON.stringify({ status }),
        },
        token
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminUsers"] });
    },
  });
}

export function useUnlockStack() {
  const { token } = useAuth();

  return useMutation({
    mutationFn: async ({ stackName }) => {
      return fetchWithAuth(
        `${API_BASE_URL}/api/v1/admin/unlock-stack`,
        {
          method: "POST",
          body: JSON.stringify({ stackName }),
        },
        token
      );
    },
  });
}

export function useBackendLogs() {
  const { token } = useAuth();

  return useQuery({
    queryKey: ["backendLogs"],
    queryFn: async () => {
      const data = await fetchWithAuth(`${API_BASE_URL}/api/v1/admin/logs?lines=300`, {}, token);
      return data;
    },
    refetchInterval: 5000,
  });
}

